import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useInterview } from '../contexts/InterviewContext';

const SessionGuard = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionId = new URLSearchParams(location.search).get('session_id');
  const { updateInterviewData } = useInterview();

  const [ready, setReady] = useState(false);
  const hasValidatedRef = useRef(false);

  useEffect(() => {
    if (hasValidatedRef.current) return;
    hasValidatedRef.current = true;

    const validate = async () => {
      if (!sessionId) return navigate('/setup');

      try {
        //console.log('validateSession');

        const { valid, data } = await api.validateSession(sessionId);

        if (!valid) {
          //console.log('Invalid session. Redirecting...');
          localStorage.removeItem('interview_session_id');
          sessionStorage.clear();
          navigate('/session-ended', { replace: true });
          return;
        }

        const { status, payment_url, session } = data;

        if (status === 'pending_payment') {
          //console.log('💰 Payment pending. Redirecting...');
          window.location.href = payment_url;
          return;
        }

        if (session.status === 'expired') {
          //console.log('⏱️ Session expired. Redirecting...');
          navigate('/session-ended', { replace: true });
          return;
        }

        // Convert duration_left to seconds
        let totalSeconds;
        if (typeof session.duration_left === 'number') {
          totalSeconds = session.duration_left;
        } else if (
          typeof session.duration_left === 'string' &&
          session.duration_left.includes(':')
        ) {
          const [min, sec] = session.duration_left.split(':').map(Number);
          totalSeconds = min * 60 + sec;
        } else {
          totalSeconds = session.duration * 60;
        }

        // Save session data to global context
        updateInterviewData((prev) => ({
          ...prev,
          session_id: sessionId,
          chatHistory: session.chatHistory || [],
          ...session,
          timeLeft: totalSeconds,
          initialTime: totalSeconds,
        }));

        setReady(true);
      } catch (err) {
        //console.error('Session validation error:', err);
        navigate('/session-ended', { replace: true });
      }
    };

    validate();

    const blockBack = () => window.history.pushState(null, '', window.location.href);
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', blockBack);

    return () => window.removeEventListener('popstate', blockBack);
  }, [sessionId, navigate, updateInterviewData]);

  return ready ? children : null;
};

export default SessionGuard;
